<template>
    <v-container fluid id="widget-dashboard-frame" class="widget-dashboard widget-component">
        <v-container class="dashboard-container">
            <main-menu
                v-if="normalizedOptions.menu.show"
                :root-url="rootUrl"
                :loginDialog="loginDialog"
                :accessToken="accessToken"
                :show-login-if-logged-out="false"
                @loggedIn="handleLoginEmit"
                :show-elements="normalizedOptions.menu.show"
                @showLoginDialog="handleLoginDialogEmit"
            >
            </main-menu>
            <v-row v-if="normalizedOptions.title.show">
                <v-col cols="12">
                    <h1 class="mb-4">
                        {{ normalizedOptions.title.text }}
                    </h1>
                </v-col>
            </v-row>
            <v-row v-if="loggedIn">
                <v-col cols="12">
                    <v-tabs
                        v-model="activeTab"
                        slider-color="primary"
                    >
                        <v-tab v-if="normalizedOptions.applications.enabled" href="#applications">
                            {{ normalizedOptions.applications.title.text }}
                        </v-tab>
                        <v-tab v-if="normalizedOptions.viewings.enabled" href="#viewings">
                            {{ normalizedOptions.viewings.title.text }}
                        </v-tab>
                        <v-tab v-if="normalizedOptions.favourites.enabled" href="#favourites">
                            {{ normalizedOptions.favourites.title.text }}
                        </v-tab>
                        <v-tab v-if="normalizedOptions.profile.enabled" href="#profile">
                            {{ normalizedOptions.profile.title.text }}
                        </v-tab>
                    </v-tabs>
                </v-col>
            </v-row>
            <v-row v-else>
                <v-col cols="12">
                    <p>Please log in to continue</p>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-tabs-items v-model="activeTab">
                        <v-tab-item v-if="normalizedOptions.applications.enabled" value="applications">
                            <v-card flat>
                                <Applications
                                    :key="applicationsRefresh"
                                    :root-url="rootUrl"
                                    :access-token="token"
                                    :settings="normalizedOptions.applications"
                                    :listings-url="listingsUrl"
                                    @changeTab="handleChangeTab"
                                    @update="updateApplications"
                                >
                                </Applications>
                            </v-card>
                        </v-tab-item>
                        <v-tab-item v-if="normalizedOptions.viewings.enabled" value="viewings">
                            <v-card flat>
                                <Viewings
                                    :key="viewingsRefresh"
                                    :root-url="rootUrl"
                                    :access-token="token"
                                    :settings="normalizedOptions.viewings"
                                    :listings-url="listingsUrl"
                                    @changeTab="handleChangeTab"
                                    @update="updateViewings"
                                >
                                </Viewings>
                            </v-card>
                        </v-tab-item>
                        <v-tab-item v-if="normalizedOptions.favourites.enabled" value="favourites">
                            <v-card flat>
                                Favourites
                            </v-card>
                        </v-tab-item>
                        <v-tab-item v-if="normalizedOptions.profile.enabled" value="profile">
                            <v-card flat>
                                <my-profile
                                    :key="profileRefresh"
                                    :root-url="rootUrl"
                                    :access-token="token"
                                    :settings="normalizedOptions.profile"
                                    @changeTab="handleChangeTab"
                                    @update="updateProfile"
                                >
                                </my-profile>
                            </v-card>
                        </v-tab-item>
                    </v-tabs-items>
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script>
import {defineComponent, ref, onMounted, watch, computed} from "@vue/composition-api";
import {has, merge} from "lodash";
import LoginDialog from "./partials/LoginDialog";
import Applications from "./Applications";
import Viewings from "./Viewings";
import MainMenu from "./partials/MainMenu";
import ProfileService from "../services/ProfileService";

export default defineComponent({
    name: "Dashboard",
    components: {LoginDialog, Applications, Viewings, MainMenu},
    props: {
        rootUrl: {
            type: String,
            required: true
        },
        settings: {
            type: Object
        },
        accessToken: {
            type: String
        },
        listingsUrl: {
            type: String,
            default: null
        },
    },
    setup(props) {
        const loading = ref(false);
        const applications = ref([]);
        const loggedInProfile = ref(false);
        const loggedIn = ref(false);

        const token = ref(null);
        const loginDialog = ref(false);
        const showLoginDialogCloseButton = ref(true);
        const activeTab = ref("");

        const applicationsRefresh = ref(0);
        const viewingsRefresh = ref(0);
        const favouritesRefresh = ref(0);
        const profileRefresh = ref(0);

        const defaultSettings = ref({
            menu: {
                show: true
            },
            title: {
                show: true,
                text: 'Dashboard',
            },
            applications: {
                enabled: true,
                title: {
                    show: true,
                    text: 'Applications',
                },
                menu: {
                    show: true
                },
            },
            viewings: {
                enabled: false,
                title: {
                    show: true,
                    text: 'Appointments',
                },
                menu: {
                    show: true
                },
            },
            favourites: {
                enabled: false,
                title: {
                    show: true,
                    text: 'Favourites',
                },
                menu: {
                    show: true
                },
            },
            profile: {
                enabled: true,
                title: {
                    show: true,
                    text: 'Profile',
                },
                menu: {
                    show: true
                },
            },
            events: {
                redirectIfNotLoggedIn: '/login',
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        watch(activeTab, currentValue => {
            updateComponent(currentValue);
        });

        function updateComponent(component) {
            if (component == "applications") {
                applicationsRefresh.value++;
            } else if (component == "viewings") {
                viewingsRefresh.value++;
            } else if (component == "favourites") {
                favouritesRefresh.value++;
            } else if (component == "profile") {
                profileRefresh.value++;
            }
        }

        function handleLoginEmit(data) {
            if (data.loggedInProfile) {
                loggedInProfile.value = data.loggedInProfile;
            }
            if (data.accessToken) {
                token.value = data.accessToken;
            }
            activeTab.value = "applications";
        }

        function handleLoginDialogEmit(val) {
            loginDialog.value = val;
        }

        function handleChangeTab(val) {
            activeTab.value = val;
        }

        function redirectToLogin() {
            if (has(normalizedOptions.value, 'events.redirectIfNotLoggedIn')) {
                window.location.href = normalizedOptions.value.events.redirectIfNotLoggedIn;
            }
        }

        function updateApplications() {
            applicationsRefresh.value = applicationsRefresh.value + 1;
        }

        function updateViewings() {
            viewingsRefresh.value = viewingsRefresh.value + 1;
        }

        function updateViewings() {
            viewingsRefresh.value = viewingsRefresh.value + 1;
        }

        function updateProfile() {
            profileRefresh.value = profileRefresh.value + 1;
        }

        onMounted(() => {
            init();
        });

        function init() {
            loggedIn.value = false;
            ProfileService.getProfile()
                .then(response => {
                    loggedIn.value = true;
                    loggedInProfile.value = response;
                })
                .catch(error => {
                    redirectToLogin();
                });
        }

        return {
            // Variables
            applicationsRefresh,
            viewingsRefresh,
            favouritesRefresh,
            profileRefresh,
            token,
            loading,
            applications,
            loginDialog,
            loggedInProfile,
            activeTab,
            showLoginDialogCloseButton,
            normalizedOptions,
            loggedIn,
            // Functions
            handleLoginEmit,
            handleLoginDialogEmit,
            handleChangeTab,
            updateApplications,
            updateViewings,
            updateProfile
        };
    }
});
</script>
<style>
.dashboard-container {
    min-height: 60vh;
}
</style>
